import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type UserRole = 'creator' | 'reviewer' | 'approver' | 'admin' | 'viewer';

export interface Profile {
  id: string;
  name: string;
  email: string;
  designation?: string;
  company_id: string;
  is_first_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface Company {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  allow_pdf_preview?: boolean;
  cover_image_url?: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  company: Company | null;
  roles: UserRole[];
  loading: boolean;
  accessDenied: boolean;
  accessError: any;
  signUp: (email: string, password: string, name: string, company: string, designation?: string) => Promise<{ error: any }>;
  signUpUser: (email: string, password: string, name: string, designation: string, invitationToken: string) => Promise<{ error: any }>;
  checkInvitation: (email: string) => Promise<{ hasInvitation: boolean; invitation: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  inviteUser: (email: string, role: UserRole) => Promise<{ error: any }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessError, setAccessError] = useState<any>(null);
  const fetchingProfileRef = useRef<string | null>(null);

  const fetchProfile = async (userId: string) => {
    // Prevent duplicate concurrent fetches for the same user
    if (fetchingProfileRef.current === userId) {
      return;
    }
    
    fetchingProfileRef.current = userId;
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      // Check if profile doesn't exist or there's a specific error
      if (profileError || !profileData) {
        console.error('Error fetching profile:', profileError);
        setAccessDenied(true);
        setAccessError({
          type: 'profile_not_found',
          message: profileError?.message || 'Profile not found',
          details: profileError
        });
        setProfile(null);
        setCompany(null);
        setRoles([]);
        return;
      }

      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
      }

      setProfile(profileData);
      setAccessDenied(false);
      setAccessError(null);
      
      if (profileData?.company_id) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', profileData.company_id)
          .maybeSingle();
        
        if (companyError) {
          console.error('Error fetching company:', companyError);
        }
        
        setCompany(companyData);
      } else {
        setCompany(null);
      }
      
      setRoles(rolesData?.map(r => r.role as UserRole) || []);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setAccessDenied(true);
      setAccessError({
        type: 'unexpected_error',
        message: 'Unexpected error occurred',
        details: error
      });
      setProfile(null);
      setCompany(null);
      setRoles([]);
    } finally {
      fetchingProfileRef.current = null;
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Reset access denied state for new session
          setAccessDenied(false);
          setAccessError(null);
          // Defer profile fetching to avoid blocking auth state change
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setCompany(null);
          setRoles([]);
          setAccessDenied(false);
          setAccessError(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    // Note: onAuthStateChange will fire with INITIAL_SESSION event, so we don't need to fetch profile here
    // This prevents duplicate profile fetches on initial load
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      // Profile will be fetched by onAuthStateChange INITIAL_SESSION event
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, name: string, company: string, designation?: string) => {
    // Check if a company with this name already exists
    const { data: existingCompany, error: companyError } = await supabase
      .from('companies')
      .select('id, name')
      .eq('name', company)
      .maybeSingle();

    if (companyError) {
      return { error: { message: 'Unable to verify company status' } };
    }

    // If company with this name already exists, prevent creation
    if (existingCompany) {
      return { error: { message: `A company with the name "${company}" already exists. Please choose a different name or ask the admin to invite you.` } };
    }
    
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          company,
          designation
        }
      }
    });

    if (signUpError) {
      return { error: signUpError };
    }

    // Wait a moment for the database trigger to process the user
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Try to sign in the user immediately after signup
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (signInError) {
      console.error('Auto sign-in failed:', signInError);
      return { error: { message: 'Account created but sign-in failed. Please try signing in manually.' } };
    } else {
      console.log('Auto sign-in successful:', signInData);
    }

    return { error: null };
  };

  const checkInvitation = async (email: string) => {
    const { data: invitations, error } = await supabase
      .rpc('check_invitation_by_email', { p_email: email });

    if (error || !invitations || invitations.length === 0) {
      return { hasInvitation: false, invitation: null };
    }

    // Return the first valid invitation
    return { hasInvitation: true, invitation: invitations[0] };
  };

  const signUpUser = async (email: string, password: string, name: string, designation: string, invitationToken: string) => {
    // First, verify the invitation token and get the designation from invitation
    const { data: invitations, error: invitationError } = await supabase
      .rpc('check_invitation_by_email', { p_email: email });

    if (invitationError || !invitations || invitations.length === 0) {
      return { error: { message: 'No valid invitation found for this email' } };
    }

    // Find the invitation with the matching token
    const invitation = invitations.find(inv => inv.invitation_token === invitationToken);
    
    if (!invitation) {
      return { error: { message: 'Invalid invitation token' } };
    }

    // Use designation from invitation, not from form
    const invitationDesignation = invitation.designation || '';

    // Create the user account (no email confirmation needed)
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          designation: invitationDesignation,
          invitation_token: invitationToken,
          is_invited_user: true
        }
      }
    });

    if (signUpError) {
      return { error: signUpError };
    }

    // Wait a moment for the database trigger to process the user
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Try to sign in the user immediately after signup
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (signInError) {
      console.error('Auto sign-in failed:', signInError);
      return { error: { message: 'Account created but sign-in failed. Please try signing in manually.' } };
    } else {
      console.log('Auto sign-in successful:', signInData);
    }

    return { error: null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const hasRole = (role: UserRole): boolean => {
    return roles.includes(role);
  };

  const hasAnyRole = (checkRoles: UserRole[]): boolean => {
    return checkRoles.some(role => roles.includes(role));
  };

  const inviteUser = async (email: string, role: UserRole): Promise<{ error: any }> => {
    if (!profile?.company_id) {
      return { error: { message: 'No company associated with user' } };
    }

    try {
      const { error } = await supabase
        .from('user_invitations')
        .insert({
          email,
          role,
          company_id: profile.company_id,
          invited_by: profile.id
        });

      return { error };
    } catch (error) {
      return { error };
    }
  };

  const value = {
    user,
    session,
    profile,
    company,
    roles,
    loading,
    accessDenied,
    accessError,
    signUp,
    signUpUser,
    checkInvitation,
    signIn,
    signOut,
    hasRole,
    hasAnyRole,
    inviteUser,
    refreshProfile: async () => {
      if (user?.id) {
        await fetchProfile(user.id);
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};